import { useCallback } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import campaignService from '../utils/campaignService';

/**
 * Hook for managing bidirectional URL synchronization between
 * organization settings and campaign configurations
 */
export const useUrlSync = () => {
  const { currentOrganization, updateOrganization } = useOrganization();

  // Platform configuration mapping
  const PLATFORM_CONFIG = {
    google: {
      orgField: 'google_review_url',
      label: 'Google Business Profile',
      placeholder: 'https://g.page/your-business/review'
    },
    yelp: {
      orgField: 'yelp_url', 
      label: 'Yelp Business',
      placeholder: 'https://www.yelp.com/biz/your-business'
    },
    facebook: {
      orgField: 'facebook_url',
      label: 'Facebook Page', 
      placeholder: 'https://www.facebook.com/your-business'
    }
  };

  /**
   * Get the effective URL for a platform (campaign override or org default)
   */
  const getEffectiveUrl = useCallback((platform, campaignPlatformUrls = {}) => {
    const campaignUrl = campaignPlatformUrls[platform];
    const orgUrl = currentOrganization?.[PLATFORM_CONFIG[platform]?.orgField];
    
    return {
      url: campaignUrl || orgUrl || '',
      source: campaignUrl ? 'campaign' : orgUrl ? 'organization' : 'none',
      isOverride: !!campaignUrl,
      orgDefault: orgUrl || ''
    };
  }, [currentOrganization]);

  /**
   * Get all platform URLs with their sources
   */
  const getAllEffectiveUrls = useCallback((campaignPlatformUrls = {}) => {
    return Object.keys(PLATFORM_CONFIG).reduce((acc, platform) => {
      acc[platform] = getEffectiveUrl(platform, campaignPlatformUrls);
      return acc;
    }, {});
  }, [getEffectiveUrl]);

  /**
   * Sync URL from campaign to organization (save as default)
   */
  const syncToOrganization = useCallback(async (platform, url) => {
    if (!currentOrganization || !PLATFORM_CONFIG[platform]) {
      throw new Error('Invalid platform or no organization selected');
    }

    const orgField = PLATFORM_CONFIG[platform].orgField;
    const result = await updateOrganization({ [orgField]: url });
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update organization');
    }

    return result;
  }, [currentOrganization, updateOrganization]);

  /**
   * Sync URL from organization to campaign (inherit default)
   */
  const syncToCampaign = useCallback(async (platform, campaignPlatformUrls = {}) => {
    if (!currentOrganization || !PLATFORM_CONFIG[platform]) {
      throw new Error('Invalid platform or no organization selected');
    }

    const orgUrl = currentOrganization[PLATFORM_CONFIG[platform].orgField] || '';
    
    // Create updated platform URLs with org default
    const updatedPlatformUrls = {
      ...campaignPlatformUrls,
      [platform]: orgUrl
    };

    // Remove the platform URL if it matches org default (remove override)
    if (orgUrl === '') {
      delete updatedPlatformUrls[platform];
    }

    return updatedPlatformUrls;
  }, [currentOrganization]);

  /**
   * Sync all URLs from organization to campaign
   */
  const syncAllToCampaign = useCallback((campaignPlatformUrls = {}) => {
    if (!currentOrganization) {
      return campaignPlatformUrls;
    }

    const updatedUrls = { ...campaignPlatformUrls };
    
    Object.keys(PLATFORM_CONFIG).forEach(platform => {
      const orgUrl = currentOrganization[PLATFORM_CONFIG[platform].orgField] || '';
      if (orgUrl) {
        updatedUrls[platform] = orgUrl;
      } else {
        // Remove platform URL if org has no default
        delete updatedUrls[platform];
      }
    });

    return updatedUrls;
  }, [currentOrganization]);

  /**
   * Sync all URLs from campaign to organization
   */
  const syncAllToOrganization = useCallback(async (campaignPlatformUrls = {}) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    const updates = {};
    Object.keys(PLATFORM_CONFIG).forEach(platform => {
      const campaignUrl = campaignPlatformUrls[platform];
      if (campaignUrl) {
        updates[PLATFORM_CONFIG[platform].orgField] = campaignUrl;
      }
    });

    if (Object.keys(updates).length === 0) {
      return { success: true, message: 'No URLs to sync' };
    }

    const result = await updateOrganization(updates);
    if (!result.success) {
      throw new Error(result.error || 'Failed to sync URLs to organization');
    }

    return result;
  }, [currentOrganization, updateOrganization]);

  /**
   * Check if there are differences between campaign and org URLs
   */
  const hasUrlDifferences = useCallback((campaignPlatformUrls = {}) => {
    if (!currentOrganization) return false;

    return Object.keys(PLATFORM_CONFIG).some(platform => {
      const campaignUrl = campaignPlatformUrls[platform] || '';
      const orgUrl = currentOrganization[PLATFORM_CONFIG[platform].orgField] || '';
      return campaignUrl !== orgUrl;
    });
  }, [currentOrganization]);

  /**
   * Get summary of URL differences
   */
  const getUrlDifferences = useCallback((campaignPlatformUrls = {}) => {
    if (!currentOrganization) return {};

    const differences = {};
    Object.keys(PLATFORM_CONFIG).forEach(platform => {
      const campaignUrl = campaignPlatformUrls[platform] || '';
      const orgUrl = currentOrganization[PLATFORM_CONFIG[platform].orgField] || '';
      
      if (campaignUrl !== orgUrl) {
        differences[platform] = {
          campaign: campaignUrl,
          organization: orgUrl,
          type: campaignUrl && orgUrl ? 'different' : 
                campaignUrl ? 'campaign-only' : 'org-only'
        };
      }
    });

    return differences;
  }, [currentOrganization]);

  return {
    // Core sync functions
    syncToOrganization,
    syncToCampaign,
    syncAllToOrganization,
    syncAllToCampaign,
    
    // URL resolution
    getEffectiveUrl,
    getAllEffectiveUrls,
    
    // Difference detection
    hasUrlDifferences,
    getUrlDifferences,
    
    // Platform configuration
    PLATFORM_CONFIG,
    platforms: Object.keys(PLATFORM_CONFIG)
  };
};

export default useUrlSync;